// GearTile.ts
//
// Define all the enums and types needed for gear tiles. Also
// define any methods needed to generate and manipulate this data.

import Tile from 'app/src/engine/tiles/Tile';

export const tileName = 'GearTile';

export enum GearTileDirectionError {
	InvalidDirection,
}

export enum GearTileDirection {
	Clockwise,
	CounterClockwise,
}

export function getGearTileDirection(name: string): GearTileDirection {
	switch (name) {
		case 'Clockwise':
			return GearTileDirection.Clockwise;
		case 'CounterClockwise':
			return GearTileDirection.CounterClockwise;
		default:
			throw GearTileDirectionError.InvalidDirection;
	}
}

export enum GearTileFloorTypeError {
	InvalidFloorType,
}

export enum GearTileFloorType {
	Type1,
	Type2,
	Type3,
	Type4,
}

export function getGearTileFloorType(name: string): GearTileFloorType {
	switch (name) {
		case 'Type1':
			return GearTileFloorType.Type1;
		case 'Type2':
			return GearTileFloorType.Type2;
		case 'Type3':
			return GearTileFloorType.Type3;
		case 'Type4':
			return GearTileFloorType.Type4;
		default:
			throw GearTileFloorTypeError.InvalidFloorType;
	}
}

export enum GearTileFloorOrientationError {
	InvalidFloorOrientation,
}

export enum GearTileFloorOrientation {
	Up,
	Down,
	Left,
	Right,
}

export function getGearTileFloorOrientation(name: string): GearTileFloorOrientation {
	switch (name) {
		case 'Up':
			return GearTileFloorOrientation.Up;
		case 'Right':
			return GearTileFloorOrientation.Right;
		case 'Down':
			return GearTileFloorOrientation.Down;
		case 'Left':
			return GearTileFloorOrientation.Left;
		default:
			throw GearTileFloorOrientationError.InvalidFloorOrientation;
	}
}

export default class GearTile extends Tile {
	get tileName(): string {
		return tileName;
	}

	private _direction: GearTileDirection;
	get direction(): GearTileDirection {
		return this._direction;
	}

	private _floorType: GearTileFloorType;
	get floorType(): GearTileFloorType {
		return this._floorType;
	}

	private _floorOrientation: GearTileFloorOrientation;
	get floorOrientation(): GearTileFloorOrientation {
		return this._floorOrientation;
	}

	constructor(
		direction: GearTileDirection,
		floorType: GearTileFloorType,
		floorOrientation: GearTileFloorOrientation,
	) {
		super();

		// Save params.
		this._direction = direction;
		this._floorType = floorType;
		this._floorOrientation = floorOrientation;
	}

	public isEqualTo(tile: GearTile): boolean {
		return (
			tile.direction === this.direction &&
			tile.floorType === this.floorType &&
			tile.floorOrientation === this.floorOrientation
		);
	}

	public clone(): GearTile {
		const clone = new GearTile(this.direction, this.floorType, this.floorOrientation);
		clone.walls = new Set(this.walls);
		clone.allowsMultipleMovableItems = this.allowsMultipleMovableItems;
		return clone;
	}
}

export function rotateGearTile(tile: GearTile, clockwise: boolean): GearTile {
	let orientation = GearTileFloorOrientation.Right;
	let floorType = tile.floorType;
	const floorOrientation = tile.floorOrientation;

	if (clockwise) {
		let moveToNextType = false;

		switch (floorOrientation) {
			case GearTileFloorOrientation.Up:
				orientation = GearTileFloorOrientation.Right;
				break;
			case GearTileFloorOrientation.Right:
				orientation = GearTileFloorOrientation.Down;
				break;
			case GearTileFloorOrientation.Down:
				orientation = GearTileFloorOrientation.Left;
				break;
			case GearTileFloorOrientation.Left:
				orientation = GearTileFloorOrientation.Up;
				moveToNextType = true;
				break;
		}

		if (moveToNextType) {
			switch (floorType) {
				case GearTileFloorType.Type1:
					floorType = GearTileFloorType.Type2;
					break;
				case GearTileFloorType.Type2:
					floorType = GearTileFloorType.Type3;
					break;
				case GearTileFloorType.Type3:
					floorType = GearTileFloorType.Type4;
					break;
				case GearTileFloorType.Type4:
					floorType = GearTileFloorType.Type1;
					break;
			}
		}
	} else {
		let moveToPreviousType = false;

		switch (floorOrientation) {
			case GearTileFloorOrientation.Up:
				orientation = GearTileFloorOrientation.Left;
				moveToPreviousType = true;
				break;
			case GearTileFloorOrientation.Right:
				orientation = GearTileFloorOrientation.Up;
				break;
			case GearTileFloorOrientation.Down:
				orientation = GearTileFloorOrientation.Right;
				break;
			case GearTileFloorOrientation.Left:
				orientation = GearTileFloorOrientation.Down;
				break;
		}

		if (moveToPreviousType) {
			switch (floorType) {
				case GearTileFloorType.Type1:
					floorType = GearTileFloorType.Type4;
					break;
				case GearTileFloorType.Type2:
					floorType = GearTileFloorType.Type1;
					break;
				case GearTileFloorType.Type3:
					floorType = GearTileFloorType.Type2;
					break;
				case GearTileFloorType.Type4:
					floorType = GearTileFloorType.Type3;
					break;
			}
		}
	}

	return new GearTile(tile.direction, floorType, orientation);
}
